import React, { useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { API_LOGIN } from "../../constants/api";
import { postRequest } from "../../services/axios";
import { loginValidation } from "../../validations/loginValidation";
import { FormValues } from "./index.type";

const LoginForm = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (formValues: FormValues) => {
    setIsLoading(true); // Set loading to true on form submission
    try {
      const { data } = await postRequest(API_LOGIN, formValues);

      if (data) {
        toast.success("Sign in Successfully", { position: "top-right" });
        localStorage.setItem("token", data);
        navigate("/users");
      }
    } catch (error: any) {
      toast.error(error?.response?.data, { position: "top-right" });
    } finally {
      setIsLoading(false); // Set loading to false after form submission, whether success or failure
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  return (
    <Formik
      initialValues={{
        email: "",
        password: "",
      }}
      validationSchema={loginValidation}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isValid, dirty, isSubmitting }) => (
        <Form className="space-y-4 md:space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >
              Your email
            </label>
            <Field
              className={`${
                isSubmitting ? "bg-gray-400" : "bg-gray-50"
              } border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
              id="email"
              name="email"
              placeholder="email"
              disabled={isSubmitting}
            />
            <ErrorMessage
              name="email"
              render={(msg) => (
                <span className="text-sm text-red-800">{msg}</span>
              )}
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >
              Password
            </label>
            <div className="relative">
              <Field
                className={`${"bg-gray-50"} border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
                id="password"
                name="password"
                placeholder="password"
                type={showPassword ? "text" : "password"}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-1 flex items-center px-3 text-gray-700 dark:text-white"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="18"
                    id="eye"
                  >
                    <g
                      fill="none"
                      fill-rule="evenodd"
                      stroke="#000"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      transform="translate(1 1)"
                    >
                      <path d="M0 8s4-8 11-8 11 8 11 8-4 8-11 8S0 8 0 8z"></path>
                      <circle cx="11" cy="8" r="3"></circle>
                    </g>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    id="eye"
                  >
                    <g
                      fill="none"
                      fill-rule="evenodd"
                      stroke="#000"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"></path>
                    </g>
                  </svg>
                )}
              </button>
            </div>
            <ErrorMessage
              name="password"
              render={(msg) => (
                <span className="text-sm text-red-800">{msg}</span>
              )}
            />
          </div>
          <div className="flex items-center justify-end">
            <a
              href="/"
              className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-500"
            >
              Forgot password?
            </a>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full text-white ${
              isSubmitting ? "bg-primary-500" : "bg-blueDark"
            } font-medium rounded-lg text-sm px-5 py-2.5 text-center`}
          >
            {isSubmitting && (
              <svg
                aria-hidden="true"
                role="status"
                className="inline w-4 h-4 mr-3 text-white animate-spin"
                viewBox="0 0 100 101"
                fill="none"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="#E5E7EB"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentColor"
                />
              </svg>
            )}
            {isSubmitting ? "Signing In" : "Sign in"}
          </button>
        </Form>
      )}
    </Formik>
  );
};

export default LoginForm;
